/* eslint-disable @typescript-eslint/no-explicit-any -- form.watch() and dailyContents have dynamic shapes */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  learningPlanApi, 
  lessonApi, 
  tipApi, 
  hackApi, 
  tidbitApi, 
  nanoApi, 
  assessmentApi, 
  nudgeApi 
} from '@/services/learningManagementApi';
import { winApi, skillApi, painPointApi } from '@/services/skillKnowledgeApi';
import { useEntity, useMutation } from '@/hooks';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { LoadingState, ErrorState } from '@/components/common';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Save, Plus, Trash2, ExternalLink, X } from 'lucide-react';
import type {
  LearningPlan,
  PathPlan,
  PathType,
  Status,
  DifficultyLevel,
  GenerationSource,
  CreateLearningPlanRequest,
  UpdateLearningPlanRequest,
} from '@/types/learningManagement';
import type { Win, Skill, PainPoint } from '@/types/skillKnowledge';
import type { Lesson, Tip, Hack, Tidbit, Nano, Assessment, Nudge } from '@/types/learningManagement';

const skillSetSchema = z.object({
  skillId: z.string().min(1, 'Skill ID is required'),
  skillName: z.string().min(1, 'Skill name is required'),
  proficiencyLevel: z.enum(['BEGINNER', 'FOUNDATIONAL', 'BASIC', 'INTERMEDIATE', 'ADVANCED', 'EXPERT', 'MASTERY']),
});

const painPointSchema = z.object({
  painPointId: z.string().min(1, 'Pain point ID is required'),
  painPointDescription: z.string().min(1, 'Pain point description is required'),
});

const learningPlanSchema = z.object({
  title: z.string().min(3, 'Title must be at least 3 characters').max(200, 'Title must be less than 200 characters'),
  description: z.string().min(0).max(2000, 'Description must be less than 2000 characters'),
  learningPlan: z.enum(['SEVEN_DAY', 'ONE_MONTH', 'TWO_MONTH', 'THREE_MONTH', 'FIVE_MONTH', 'SIX_MONTH', 'ONE_YEAR', 'LONG_TERM', 'CUSTOM', 'PERSONALIZED']),
  planType: z.enum(['NUDGE_BASED', 'STRUCTURED', 'PERSONALIZED', 'COMBINATION']),
  status: z.enum(['DRAFT', 'ACTIVE', 'IN_PROGRESS', 'COMPLETED', 'PAUSED', 'CANCELLED', 'ARCHIVED']),
  difficultyLevel: z.enum(['BEGINNER', 'INTERMEDIATE', 'ADVANCED', 'EXPERT']),
  estimatedDurationHours: z.number().min(1).optional(),
  prerequisites: z.array(z.string()).optional(),
  win: z
    .object({
      winId: z.string().min(1, 'Win ID is required'),
      winDescription: z.string().optional(),
    })
    .optional(),
  addressingSkillSets: z.array(skillSetSchema).min(1, 'At least one skill set is required'),
  painPoints: z.array(painPointSchema).min(1, 'At least one pain point is required'),
  learningModules: z.array(z.any()), // Complex nested structure, validate at API level
  metadata: z.record(z.string(), z.unknown()).optional(),
  tags: z.array(z.string()).optional(),
  learningPlanGenerated: z.enum(['SYSTEM_DEFINED', 'AI_GENERATED', 'USER_CUSTOMIZED']),
});

type LearningPlanFormData = z.infer<typeof learningPlanSchema>;

const PATH_PLANS: PathPlan[] = ['SEVEN_DAY', 'ONE_MONTH', 'TWO_MONTH', 'THREE_MONTH', 'FIVE_MONTH', 'SIX_MONTH', 'ONE_YEAR', 'LONG_TERM', 'CUSTOM', 'PERSONALIZED'];
const PATH_TYPES: PathType[] = ['NUDGE_BASED', 'STRUCTURED', 'PERSONALIZED', 'COMBINATION'];
const STATUSES: Status[] = ['DRAFT', 'ACTIVE', 'IN_PROGRESS', 'COMPLETED', 'PAUSED', 'CANCELLED', 'ARCHIVED'];
const DIFFICULTY_LEVELS: DifficultyLevel[] = ['BEGINNER', 'INTERMEDIATE', 'ADVANCED', 'EXPERT'];
const GENERATION_SOURCES: GenerationSource[] = ['SYSTEM_DEFINED', 'AI_GENERATED', 'USER_CUSTOMIZED'];

export const LearningPlanFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const { hasPermission } = useAdminAuth();
  const [wins, setWins] = useState<Win[]>([]);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [painPoints, setPainPoints] = useState<PainPoint[]>([]);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [tips, setTips] = useState<Tip[]>([]);
  const [hacks, setHacks] = useState<Hack[]>([]);
  const [tidbits, setTidbits] = useState<Tidbit[]>([]);
  const [nanos, setNanos] = useState<Nano[]>([]);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [nudges, setNudges] = useState<Nudge[]>([]);

  const form = useForm<LearningPlanFormData>({
    resolver: zodResolver(learningPlanSchema),
    defaultValues: {
      title: '',
      description: '',
      learningPlan: 'ONE_MONTH',
      planType: 'STRUCTURED',
      status: 'DRAFT',
      difficultyLevel: 'BEGINNER',
      estimatedDurationHours: undefined,
      prerequisites: [],
      win: undefined,
      addressingSkillSets: [],
      painPoints: [],
      learningModules: [],
      metadata: undefined,
      tags: [],
      learningPlanGenerated: 'USER_CUSTOMIZED',
    },
  });

  // Load all data for selection
  useEffect(() => {
    const loadData = async () => {
      try {
        const [
          winsResponse,
          skillsResponse,
          painPointsResponse,
          lessonsResponse,
          tipsResponse,
          hacksResponse,
          tidbitsResponse,
          nanosResponse,
          assessmentsResponse,
          nudgesResponse,
        ] = await Promise.all([
          winApi.list({ size: 1000 }),
          skillApi.list({ size: 1000 }),
          painPointApi.list({ size: 1000 }),
          lessonApi.list({ size: 1000 }),
          tipApi.list({ size: 1000 }),
          hackApi.list({ size: 1000 }),
          tidbitApi.list({ size: 1000 }),
          nanoApi.list({ size: 1000 }),
          assessmentApi.list({ size: 1000 }),
          nudgeApi.list({ size: 1000 }),
        ]);
        setWins(winsResponse.content || []);
        setSkills(skillsResponse.content || []);
        setPainPoints(painPointsResponse.content || []);
        setLessons(lessonsResponse.content || []);
        setTips(tipsResponse.content || []);
        setHacks(hacksResponse.content || []);
        setTidbits(tidbitsResponse.content || []);
        setNanos(nanosResponse.content || []);
        setAssessments(assessmentsResponse.content || []);
        setNudges(nudgesResponse.content || []);
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    loadData();
  }, []);

  const {
    fields: skillSetFields,
    append: appendSkillSet,
    remove: removeSkillSet,
  } = useFieldArray({
    control: form.control,
    name: 'addressingSkillSets',
  });

  const {
    fields: painPointFields,
    append: appendPainPoint,
    remove: removePainPoint,
  } = useFieldArray({
    control: form.control,
    name: 'painPoints',
  });

  // Fetch learning plan data for edit mode
  const {
    data: _learningPlan,
    isLoading: isLoadingPlan,
    error: planError,
  } = useEntity<LearningPlan>({
    fetchFn: (planId: string) => learningPlanApi.getById(planId),
    entityId: id,
    entityName: 'learningPlan',
    enabled: isEdit && !!id,
    onSuccess: (data) => {
      form.reset({
        title: data.title,
        description: data.description,
        learningPlan: (data.learningPlan || data.pathPlan) as LearningPlanFormData['learningPlan'],
        planType: (data.planType || data.pathType) as LearningPlanFormData['planType'],
        status: data.status as LearningPlanFormData['status'],
        difficultyLevel: data.difficultyLevel,
        estimatedDurationHours: data.estimatedDurationHours,
        prerequisites: data.prerequisites || [],
        win: data.win ? { winId: data.win.winId, winDescription: data.win.winDescription } : undefined,
        addressingSkillSets: data.addressingSkillSets || [],
        painPoints: data.painPoints || [],
        learningModules: data.learningModules || [],
        metadata: data.metadata,
        tags: data.tags || [],
        learningPlanGenerated: (data.learningPlanGenerated || data.learningPathGenerated) as LearningPlanFormData['learningPlanGenerated'],
      } as LearningPlanFormData);
    },
    onError: () => {
      navigate('/admin/learning-plans');
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: LearningPlanFormData) => {
      const payload: CreateLearningPlanRequest = {
        title: data.title,
        description: data.description,
        addressingSkillSets: data.addressingSkillSets,
        painPoints: data.painPoints || [],
        win: data.win || undefined,
        learningPlan: data.learningPlan,
        planType: data.planType,
        status: data.status,
        difficultyLevel: data.difficultyLevel,
        estimatedDurationHours: data.estimatedDurationHours,
        prerequisites: data.prerequisites || [],
        learningModules: data.learningModules || [],
        tags: data.tags || [],
        metadata: data.metadata,
        learningPlanGenerated: data.learningPlanGenerated,
      };
      return await learningPlanApi.create(payload);
    },
    entityName: 'learningPlan',
    operation: 'create',
    onSuccess: () => {
      navigate('/admin/learning-plans');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: LearningPlanFormData) => {
      if (!id) throw new Error('Learning Plan ID is required');
      const payload: UpdateLearningPlanRequest = {
        title: data.title,
        description: data.description,
        addressingSkillSets: data.addressingSkillSets,
        painPoints: data.painPoints || [],
        win: data.win || undefined,
        learningPlan: data.learningPlan,
        planType: data.planType,
        status: data.status,
        difficultyLevel: data.difficultyLevel,
        estimatedDurationHours: data.estimatedDurationHours,
        prerequisites: data.prerequisites || [],
        learningModules: data.learningModules || [],
        tags: data.tags || [],
        metadata: data.metadata,
        learningPlanGenerated: data.learningPlanGenerated,
      };
      return await learningPlanApi.update(id, payload);
    },
    entityName: 'learningPlan',
    operation: 'update',
    onSuccess: () => {
      navigate('/admin/learning-plans');
    },
  });

  const onSubmit = async (data: LearningPlanFormData) => {
    if (isEdit) {
      await updateMutation.mutateAsync(data);
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  const isLoading = isLoadingPlan;
  const mutation = isEdit ? updateMutation : createMutation;

  const isSystemDefined = form.watch('learningPlanGenerated') === 'SYSTEM_DEFINED';
  const canModifyGeneration = !isEdit || !isSystemDefined || hasPermission('system_admin', 'learningPlan');

  // In edit mode, show loading state while fetching
  if (isEdit && isLoading) {
    return <LoadingState message="Loading learning plan..." />;
  }

  // Show error if there's an error (only after loading completes)
  if (isEdit && planError) {
    return <ErrorState error={planError} onRetry={() => window.location.reload()} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/learning-plans">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {isEdit ? 'Edit Learning Plan' : 'Create Learning Plan'}
            </h1>
            <p className="text-muted-foreground mt-2">
              {isEdit ? 'Update learning plan information' : 'Create a new learning plan'}
            </p>
          </div>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>Enter the basic details for the learning plan</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title *</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter learning plan title" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description *</FormLabel>
                    <FormControl>
                      <textarea
                        className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        placeholder="Enter learning plan description"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="planType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Plan Type *</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select path type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {PATH_TYPES.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="learningPlan"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Learning Plan *</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select path plan" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {PATH_PLANS.map((plan) => (
                            <SelectItem key={plan} value={plan}>
                              {plan.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <FormField
                  control={form.control}
                  name="difficultyLevel"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Difficulty Level *</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select difficulty" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {DIFFICULTY_LEVELS.map((level) => (
                            <SelectItem key={level} value={level}>
                              {level}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status *</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {STATUSES.map((status) => (
                            <SelectItem key={status} value={status}>
                              {status}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="estimatedDurationHours"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Estimated Duration (Hours)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={1}
                          placeholder="Enter hours"
                          {...field}
                          onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="tags"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tags</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter tags separated by commas"
                        value={field.value?.join(', ') || ''}
                        onChange={(e) => {
                          const tags = e.target.value
                            .split(',')
                            .map((t) => t.trim())
                            .filter(Boolean);
                          field.onChange(tags);
                        }}
                      />
                    </FormControl>
                    <FormDescription>Separate multiple tags with commas</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="learningPlanGenerated"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Generation Source *</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                      disabled={!canModifyGeneration}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select generation source" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {GENERATION_SOURCES.map((source) => (
                          <SelectItem key={source} value={source}>
                            {source.replace(/_/g, ' ')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      {!canModifyGeneration && 'Cannot change generation source for system-defined plans'}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Skill Sets */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Addressing Skill Sets *</CardTitle>
                  <CardDescription>Skills addressed by this learning plan</CardDescription>
                </div>
                <Button type="button" onClick={() => appendSkillSet({ skillId: '', skillName: '', proficiencyLevel: 'BEGINNER' })}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Skill Set
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {skillSetFields.map((field, index) => (
                <div key={field.id} className="border rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">Skill Set {index + 1}</h4>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeSkillSet(index)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                  <div className="grid gap-4 md:grid-cols-3">
                    <FormField
                      control={form.control}
                      name={`addressingSkillSets.${index}.skillId`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Skill ID *</FormLabel>
                          <Select
                            onValueChange={(value) => {
                              if (value === '__none__') {
                                field.onChange('');
                              } else {
                                field.onChange(value);
                                // Auto-populate skill name if available
                                const selectedSkill = skills.find(s => s.id === value);
                                if (selectedSkill) {
                                  form.setValue(`addressingSkillSets.${index}.skillName`, selectedSkill.name || '');
                                }
                              }
                            }}
                            value={field.value || '__none__'}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select a skill" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="__none__">None</SelectItem>
                              {skills.map((skill) => (
                                <SelectItem key={skill.id} value={skill.id || ''}>
                                  {skill.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`addressingSkillSets.${index}.skillName`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Skill Name *</FormLabel>
                          <FormControl>
                            <Input placeholder="Enter skill name" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`addressingSkillSets.${index}.proficiencyLevel`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Proficiency Level *</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {['BEGINNER', 'FOUNDATIONAL', 'BASIC', 'INTERMEDIATE', 'ADVANCED', 'EXPERT', 'MASTERY'].map((level) => (
                                <SelectItem key={level} value={level}>
                                  {level}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              ))}
              {skillSetFields.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  No skill sets added. Click "Add Skill Set" to add one.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Win */}
          <Card>
            <CardHeader>
              <CardTitle>Related Win</CardTitle>
              <CardDescription>Select a win (achievement/milestone) related to this learning plan</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="win"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Win</FormLabel>
                    <Select
                      onValueChange={(value) => {
                        if (value === '__none__') {
                          field.onChange(undefined);
                        } else {
                          const currentWin = field.value;
                          field.onChange({
                            winId: value,
                            winDescription: currentWin?.winDescription || undefined,
                          });
                        }
                      }}
                      value={field.value?.winId || '__none__'}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a win (optional)" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="__none__">None</SelectItem>
                        {wins.map((win) => (
                          <SelectItem key={win.id} value={win.id || ''}>
                            {win.name}
                            {win.summary && (
                              <span className="text-muted-foreground ml-2">- {win.summary}</span>
                            )}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="win"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Win Description (Optional)</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter win description"
                        value={field.value?.winDescription || ''}
                        onChange={(e) => {
                          const currentWin = field.value;
                          if (currentWin?.winId) {
                            field.onChange({
                              ...currentWin,
                              winDescription: e.target.value || undefined,
                            });
                          }
                        }}
                        disabled={!field.value?.winId}
                      />
                    </FormControl>
                    <FormDescription>Optional description for the selected win</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Pain Points */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Pain Points *</CardTitle>
                  <CardDescription>Pain points addressed by this learning plan</CardDescription>
                </div>
                <Button type="button" onClick={() => appendPainPoint({ painPointId: '', painPointDescription: '' })}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Pain Point
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {painPointFields.map((field, index) => (
                <div key={field.id} className="border rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">Pain Point {index + 1}</h4>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removePainPoint(index)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <FormField
                      control={form.control}
                      name={`painPoints.${index}.painPointId`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Pain Point ID *</FormLabel>
                          <Select
                            onValueChange={(value) => {
                              if (value === '__none__') {
                                field.onChange('');
                              } else {
                                field.onChange(value);
                                // Auto-populate pain point description if available
                                const selectedPainPoint = painPoints.find(pp => pp.id === value);
                                if (selectedPainPoint) {
                                  form.setValue(`painPoints.${index}.painPointDescription`, selectedPainPoint.description || '');
                                }
                              }
                            }}
                            value={field.value || '__none__'}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select a pain point" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="__none__">None</SelectItem>
                              {painPoints.map((painPoint) => (
                                <SelectItem key={painPoint.id} value={painPoint.id || ''}>
                                  {painPoint.description}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`painPoints.${index}.painPointDescription`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Pain Point Description *</FormLabel>
                          <FormControl>
                            <Input placeholder="Enter pain point description" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              ))}
              {painPointFields.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  No pain points added. Click "Add Pain Point" to add one.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Learning Modules - Editable */}
          {isEdit && form.watch('learningModules') && form.watch('learningModules').length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Learning Modules ({form.watch('learningModules').length})</CardTitle>
                <CardDescription>Edit daily contents and content assignments</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {form.watch('learningModules').map((module: any, moduleIdx: number) => (
                    <div key={moduleIdx} className="border rounded-lg p-4 space-y-4">
                      <div className="grid gap-4 md:grid-cols-3">
                        {module.totalDays !== undefined && (
                          <div>
                            <Label className="text-xs text-muted-foreground">Total Days</Label>
                            <p className="text-sm font-medium">{module.totalDays}</p>
                          </div>
                        )}
                        {module.finalAssessmentId && (
                          <div>
                            <Label className="text-xs text-muted-foreground">Final Assessment ID</Label>
                            <p className="font-mono text-xs">{module.finalAssessmentId}</p>
                          </div>
                        )}
                      </div>
                      {module.dailyContents && module.dailyContents.length > 0 && (
                        <div className="mt-4 space-y-4">
                          <Label className="text-sm font-medium">Daily Contents ({module.dailyContents.length})</Label>
                          {module.dailyContents.map((content: any, contentIdx: number) => (
                            <div key={contentIdx} className="border rounded-lg p-4 space-y-4 bg-gray-50/50">
                              <div className="flex items-center justify-between">
                                <h5 className="font-medium">
                                  Day {content.dayNumber !== undefined ? content.dayNumber : contentIdx + 1}
                                  {content.title && `: ${content.title}`}
                                </h5>
                              </div>
                              
                              {/* Lesson IDs */}
                              <div>
                                <div className="flex items-center justify-between mb-2">
                                  <Label className="text-xs font-medium">Lesson IDs</Label>
                                  <a
                                    href={`/admin/lessons`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-blue-600 hover:underline"
                                  >
                                    View All Lessons
                                  </a>
                                </div>
                                <div className="space-y-2">
                                  <Select
                                    onValueChange={(value) => {
                                      if (value && value !== '__none__') {
                                        const currentIds = content.lessonIds || [];
                                        if (!currentIds.includes(value)) {
                                          const updatedModules = [...form.watch('learningModules')];
                                          updatedModules[moduleIdx].dailyContents[contentIdx].lessonIds = [...currentIds, value];
                                          form.setValue('learningModules', updatedModules);
                                        }
                                      }
                                    }}
                                    value="__none__"
                                  >
                                    <SelectTrigger className="h-8 text-xs">
                                      <SelectValue placeholder="Add Lesson" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="__none__">Select a lesson...</SelectItem>
                                      {lessons
                                        .filter((l) => !content.lessonIds?.includes(l.id || ''))
                                        .map((lesson) => (
                                          <SelectItem key={lesson.id} value={lesson.id || ''}>
                                            {lesson.title || lesson.id}
                                          </SelectItem>
                                        ))}
                                    </SelectContent>
                                  </Select>
                                  {content.lessonIds && content.lessonIds.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                      {content.lessonIds.map((id: string) => {
                                        const lesson = lessons.find((l) => l.id === id);
                                        return (
                                          <div
                                            key={id}
                                            className="flex items-center gap-1 px-2 py-1 bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 rounded text-xs"
                                          >
                                            <span className="font-mono">{lesson?.title || id}</span>
                                            <a
                                              href={`/admin/lessons/${id}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="hover:text-blue-900 dark:hover:text-blue-100 transition-colors"
                                              title="Open in new tab"
                                            >
                                              <ExternalLink className="h-3 w-3" />
                                            </a>
                                            <button
                                              type="button"
                                              onClick={() => {
                                                const updatedModules = [...form.watch('learningModules')];
                                                updatedModules[moduleIdx].dailyContents[contentIdx].lessonIds = 
                                                  content.lessonIds.filter((lid: string) => lid !== id);
                                                form.setValue('learningModules', updatedModules);
                                              }}
                                              className="ml-1 hover:text-red-600"
                                            >
                                              <X className="h-3 w-3" />
                                            </button>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Tip IDs */}
                              <div>
                                <div className="flex items-center justify-between mb-2">
                                  <Label className="text-xs font-medium">Tip IDs</Label>
                                  <a
                                    href={`/admin/tips`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-blue-600 hover:underline"
                                  >
                                    View All Tips
                                  </a>
                                </div>
                                <div className="space-y-2">
                                  <Select
                                    onValueChange={(value) => {
                                      if (value && value !== '__none__') {
                                        const currentIds = content.tipIds || [];
                                        if (!currentIds.includes(value)) {
                                          const updatedModules = [...form.watch('learningModules')];
                                          updatedModules[moduleIdx].dailyContents[contentIdx].tipIds = [...currentIds, value];
                                          form.setValue('learningModules', updatedModules);
                                        }
                                      }
                                    }}
                                    value="__none__"
                                  >
                                    <SelectTrigger className="h-8 text-xs">
                                      <SelectValue placeholder="Add Tip" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="__none__">Select a tip...</SelectItem>
                                      {tips
                                        .filter((t) => !content.tipIds?.includes(t.id || ''))
                                        .map((tip) => (
                                          <SelectItem key={tip.id} value={tip.id || ''}>
                                            {tip.title || tip.id}
                                          </SelectItem>
                                        ))}
                                    </SelectContent>
                                  </Select>
                                  {content.tipIds && content.tipIds.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                      {content.tipIds.map((id: string) => {
                                        const tip = tips.find((t) => t.id === id);
                                        return (
                                          <div
                                            key={id}
                                            className="flex items-center gap-1 px-2 py-1 bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300 rounded text-xs"
                                          >
                                            <span className="font-mono">{tip?.title || id}</span>
                                            <a
                                              href={`/admin/tips/${id}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="hover:text-green-900 dark:hover:text-green-100 transition-colors"
                                              title="Open in new tab"
                                            >
                                              <ExternalLink className="h-3 w-3" />
                                            </a>
                                            <button
                                              type="button"
                                              onClick={() => {
                                                const updatedModules = [...form.watch('learningModules')];
                                                updatedModules[moduleIdx].dailyContents[contentIdx].tipIds = 
                                                  content.tipIds.filter((tid: string) => tid !== id);
                                                form.setValue('learningModules', updatedModules);
                                              }}
                                              className="ml-1 hover:text-red-600"
                                            >
                                              <X className="h-3 w-3" />
                                            </button>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Hack IDs */}
                              <div>
                                <div className="flex items-center justify-between mb-2">
                                  <Label className="text-xs font-medium">Hack IDs</Label>
                                  <a
                                    href={`/admin/hacks`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-blue-600 hover:underline"
                                  >
                                    View All Hacks
                                  </a>
                                </div>
                                <div className="space-y-2">
                                  <Select
                                    onValueChange={(value) => {
                                      if (value && value !== '__none__') {
                                        const currentIds = content.hackIds || [];
                                        if (!currentIds.includes(value)) {
                                          const updatedModules = [...form.watch('learningModules')];
                                          updatedModules[moduleIdx].dailyContents[contentIdx].hackIds = [...currentIds, value];
                                          form.setValue('learningModules', updatedModules);
                                        }
                                      }
                                    }}
                                    value="__none__"
                                  >
                                    <SelectTrigger className="h-8 text-xs">
                                      <SelectValue placeholder="Add Hack" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="__none__">Select a hack...</SelectItem>
                                      {hacks
                                        .filter((h) => !content.hackIds?.includes(h.id || ''))
                                        .map((hack) => (
                                          <SelectItem key={hack.id} value={hack.id || ''}>
                                            {hack.title || hack.id}
                                          </SelectItem>
                                        ))}
                                    </SelectContent>
                                  </Select>
                                  {content.hackIds && content.hackIds.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                      {content.hackIds.map((id: string) => {
                                        const hack = hacks.find((h) => h.id === id);
                                        return (
                                          <div
                                            key={id}
                                            className="flex items-center gap-1 px-2 py-1 bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300 rounded text-xs"
                                          >
                                            <span className="font-mono">{hack?.title || id}</span>
                                            <a
                                              href={`/admin/hacks/${id}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="hover:text-red-900 dark:hover:text-red-100 transition-colors"
                                              title="Open in new tab"
                                            >
                                              <ExternalLink className="h-3 w-3" />
                                            </a>
                                            <button
                                              type="button"
                                              onClick={() => {
                                                const updatedModules = [...form.watch('learningModules')];
                                                updatedModules[moduleIdx].dailyContents[contentIdx].hackIds = 
                                                  content.hackIds.filter((hid: string) => hid !== id);
                                                form.setValue('learningModules', updatedModules);
                                              }}
                                              className="ml-1 hover:text-red-600"
                                            >
                                              <X className="h-3 w-3" />
                                            </button>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Tidbit IDs */}
                              <div>
                                <div className="flex items-center justify-between mb-2">
                                  <Label className="text-xs font-medium">Tidbit IDs</Label>
                                  <a
                                    href={`/admin/tidbits`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-blue-600 hover:underline"
                                  >
                                    View All Tidbits
                                  </a>
                                </div>
                                <div className="space-y-2">
                                  <Select
                                    onValueChange={(value) => {
                                      if (value && value !== '__none__') {
                                        const currentIds = content.tidbitIds || [];
                                        if (!currentIds.includes(value)) {
                                          const updatedModules = [...form.watch('learningModules')];
                                          updatedModules[moduleIdx].dailyContents[contentIdx].tidbitIds = [...currentIds, value];
                                          form.setValue('learningModules', updatedModules);
                                        }
                                      }
                                    }}
                                    value="__none__"
                                  >
                                    <SelectTrigger className="h-8 text-xs">
                                      <SelectValue placeholder="Add Tidbit" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="__none__">Select a tidbit...</SelectItem>
                                      {tidbits
                                        .filter((t) => !content.tidbitIds?.includes(t.id || ''))
                                        .map((tidbit) => (
                                          <SelectItem key={tidbit.id} value={tidbit.id || ''}>
                                            {tidbit.title || tidbit.id}
                                          </SelectItem>
                                        ))}
                                    </SelectContent>
                                  </Select>
                                  {content.tidbitIds && content.tidbitIds.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                      {content.tidbitIds.map((id: string) => {
                                        const tidbit = tidbits.find((t) => t.id === id);
                                        return (
                                          <div
                                            key={id}
                                            className="flex items-center gap-1 px-2 py-1 bg-orange-50 dark:bg-orange-950 text-orange-700 dark:text-orange-300 rounded text-xs"
                                          >
                                            <span className="font-mono">{tidbit?.title || id}</span>
                                            <a
                                              href={`/admin/tidbits/${id}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="hover:text-orange-900 dark:hover:text-orange-100 transition-colors"
                                              title="Open in new tab"
                                            >
                                              <ExternalLink className="h-3 w-3" />
                                            </a>
                                            <button
                                              type="button"
                                              onClick={() => {
                                                const updatedModules = [...form.watch('learningModules')];
                                                updatedModules[moduleIdx].dailyContents[contentIdx].tidbitIds = 
                                                  content.tidbitIds.filter((tid: string) => tid !== id);
                                                form.setValue('learningModules', updatedModules);
                                              }}
                                              className="ml-1 hover:text-red-600"
                                            >
                                              <X className="h-3 w-3" />
                                            </button>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Nano IDs */}
                              <div>
                                <div className="flex items-center justify-between mb-2">
                                  <Label className="text-xs font-medium">Nano IDs</Label>
                                  <a
                                    href={`/admin/nanos`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-blue-600 hover:underline"
                                  >
                                    View All Nanos
                                  </a>
                                </div>
                                <div className="space-y-2">
                                  <Select
                                    onValueChange={(value) => {
                                      if (value && value !== '__none__') {
                                        const currentIds = content.nanoIds || [];
                                        if (!currentIds.includes(value)) {
                                          const updatedModules = [...form.watch('learningModules')];
                                          updatedModules[moduleIdx].dailyContents[contentIdx].nanoIds = [...currentIds, value];
                                          form.setValue('learningModules', updatedModules);
                                        }
                                      }
                                    }}
                                    value="__none__"
                                  >
                                    <SelectTrigger className="h-8 text-xs">
                                      <SelectValue placeholder="Add Nano" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="__none__">Select a nano...</SelectItem>
                                      {nanos
                                        .filter((n) => !content.nanoIds?.includes(n.id || ''))
                                        .map((nano) => (
                                          <SelectItem key={nano.id} value={nano.id || ''}>
                                            {nano.title || nano.id}
                                          </SelectItem>
                                        ))}
                                    </SelectContent>
                                  </Select>
                                  {content.nanoIds && content.nanoIds.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                      {content.nanoIds.map((id: string) => {
                                        const nano = nanos.find((n) => n.id === id);
                                        return (
                                          <div
                                            key={id}
                                            className="flex items-center gap-1 px-2 py-1 bg-teal-50 dark:bg-teal-950 text-teal-700 dark:text-teal-300 rounded text-xs"
                                          >
                                            <span className="font-mono">{nano?.title || id}</span>
                                            <a
                                              href={`/admin/nanos/${id}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="hover:text-teal-900 dark:hover:text-teal-100 transition-colors"
                                              title="Open in new tab"
                                            >
                                              <ExternalLink className="h-3 w-3" />
                                            </a>
                                            <button
                                              type="button"
                                              onClick={() => {
                                                const updatedModules = [...form.watch('learningModules')];
                                                updatedModules[moduleIdx].dailyContents[contentIdx].nanoIds = 
                                                  content.nanoIds.filter((nid: string) => nid !== id);
                                                form.setValue('learningModules', updatedModules);
                                              }}
                                              className="ml-1 hover:text-red-600"
                                            >
                                              <X className="h-3 w-3" />
                                            </button>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Nudge IDs */}
                              <div>
                                <div className="flex items-center justify-between mb-2">
                                  <Label className="text-xs font-medium">Nudge IDs</Label>
                                  <a
                                    href={`/admin/nudges`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-blue-600 hover:underline"
                                  >
                                    View All Nudges
                                  </a>
                                </div>
                                <div className="space-y-2">
                                  <Select
                                    onValueChange={(value) => {
                                      if (value && value !== '__none__') {
                                        const currentIds = content.nudgeIds || [];
                                        if (!currentIds.includes(value)) {
                                          const updatedModules = [...form.watch('learningModules')];
                                          updatedModules[moduleIdx].dailyContents[contentIdx].nudgeIds = [...currentIds, value];
                                          form.setValue('learningModules', updatedModules);
                                        }
                                      }
                                    }}
                                    value="__none__"
                                  >
                                    <SelectTrigger className="h-8 text-xs">
                                      <SelectValue placeholder="Add Nudge" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="__none__">Select a nudge...</SelectItem>
                                      {nudges
                                        .filter((n) => !content.nudgeIds?.includes(n.id || ''))
                                        .map((nudge) => (
                                          <SelectItem key={nudge.id} value={nudge.id || ''}>
                                            {nudge.title || nudge.id}
                                          </SelectItem>
                                        ))}
                                    </SelectContent>
                                  </Select>
                                  {content.nudgeIds && content.nudgeIds.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                      {content.nudgeIds.map((id: string) => {
                                        const nudge = nudges.find((n) => n.id === id);
                                        return (
                                          <div
                                            key={id}
                                            className="flex items-center gap-1 px-2 py-1 bg-purple-50 dark:bg-purple-950 text-purple-700 dark:text-purple-300 rounded text-xs"
                                          >
                                            <span className="font-mono">{nudge?.title || id}</span>
                                            <a
                                              href={`/admin/nudges/${id}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="hover:text-purple-900 dark:hover:text-purple-100 transition-colors"
                                              title="Open in new tab"
                                            >
                                              <ExternalLink className="h-3 w-3" />
                                            </a>
                                            <button
                                              type="button"
                                              onClick={() => {
                                                const updatedModules = [...form.watch('learningModules')];
                                                updatedModules[moduleIdx].dailyContents[contentIdx].nudgeIds = 
                                                  content.nudgeIds.filter((nid: string) => nid !== id);
                                                form.setValue('learningModules', updatedModules);
                                              }}
                                              className="ml-1 hover:text-red-600"
                                            >
                                              <X className="h-3 w-3" />
                                            </button>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Assessment ID */}
                              <div>
                                <div className="flex items-center justify-between mb-2">
                                  <Label className="text-xs font-medium">Assessment ID</Label>
                                  <a
                                    href={`/admin/assessments`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-blue-600 hover:underline"
                                  >
                                    View All Assessments
                                  </a>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Select
                                    onValueChange={(value) => {
                                      const updatedModules = [...form.watch('learningModules')];
                                      updatedModules[moduleIdx].dailyContents[contentIdx].assessmentId = 
                                        value === '__none__' ? undefined : value;
                                      form.setValue('learningModules', updatedModules);
                                    }}
                                    value={content.assessmentId || '__none__'}
                                  >
                                    <SelectTrigger className="h-8 text-xs flex-1">
                                      <SelectValue placeholder="Select assessment" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="__none__">None</SelectItem>
                                      {assessments.map((assessment) => (
                                        <SelectItem key={assessment.id} value={assessment.id || ''}>
                                          {assessment.title || assessment.id}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  {content.assessmentId && (
                                    <a
                                      href={`/admin/assessments/${content.assessmentId}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-indigo-600 hover:text-indigo-900 transition-colors"
                                      title="Open in new tab"
                                    >
                                      <ExternalLink className="h-4 w-4" />
                                    </a>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" asChild>
              <Link to="/admin/learning-plans">Cancel</Link>
            </Button>
            <Button type="submit" disabled={mutation.isLoading}>
              <Save className="mr-2 h-4 w-4" />
              {mutation.isLoading ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default LearningPlanFormPage;
